import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Cross-event leaderboard for a single Subarchetype. Lists every (event,
 * player) where a player brought a deck of this subarchetype, newest event
 * first, with player, deck, finishing rank and points.
 *
 * The "← Voltar" link uses browser history rather than a fixed target so
 * the page works the same whether the user came from an event page, the
 * matches page or a future entry point — there's no canonical parent.
 */
export function SubarchetypePage() {
  const { subarchetypeId } = useParams<{ subarchetypeId: string }>();
  const id = Number(subarchetypeId);
  const navigate = useNavigate();

  const dataQuery = useQuery({
    queryKey: ["subarchetypeResults", id],
    queryFn: () => api.subarchetypeResults(id),
    enabled: Number.isFinite(id),
  });

  const subarchetypeName = dataQuery.data?.subarchetype.name;
  usePageTitle(
    subarchetypeName ? `Subarquétipo — ${subarchetypeName}` : null,
  );

  if (dataQuery.isLoading) return <Loading />;
  if (dataQuery.error) {
    const err = dataQuery.error as Error;
    if (/404/.test(err.message)) {
      return (
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm font-medium text-brand-700 hover:underline"
          >
            ← Voltar
          </button>
          <div className="rounded-2xl border border-line bg-white p-8 text-center text-[#6b6356]">
            <h2 className="font-display text-xl font-bold text-ink mb-2">
              Subarquétipo não encontrado
            </h2>
          </div>
        </div>
      );
    }
    return <ErrorBox error={err} />;
  }
  if (!dataQuery.data)
    return <ErrorBox error={new Error("Subarchetype not found")} />;

  const { subarchetype, results } = dataQuery.data;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          ← Voltar
        </button>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink">
            {subarchetype.name}
          </h1>
          <div className="mt-2">
            <span className="inline-block rounded-full bg-brand-50 text-brand-900 text-xs font-semibold px-2 py-0.5">
              {subarchetype.archetypeName}
            </span>
          </div>
        </div>
      </header>

      <section className="space-y-3">
        <div className="text-sm text-[#8a8170]">
          {results.length} resultado(s) em todos os eventos
        </div>
        <div className="rounded-2xl border border-line bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-bold">Data</th>
                <th className="text-left px-4 py-3 font-bold">Evento</th>
                <th className="text-left px-4 py-3 font-bold">Player</th>
                <th className="text-left px-4 py-3 font-bold">Deck</th>
                <th className="text-right px-4 py-3 w-16 font-bold">#</th>
                <th className="text-right px-4 py-3 w-24 font-bold">Pontos</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.eventResultId} className="border-t border-[#f3efe6]">
                  <td className="px-4 py-2.5 text-[#9a9182] tabular-nums whitespace-nowrap">
                    {r.eventDate}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/events/${r.eventId}`}
                      className="text-brand-700 hover:underline font-medium"
                    >
                      {r.eventName}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-ink">
                    {r.playerName}
                  </td>
                  <td className="px-4 py-2.5 text-[#6b6356]">
                    {r.deckName ? (
                      r.hasDecklist ? (
                        <Link
                          to={`/events/${r.eventId}/decks/${r.eventResultId}`}
                          className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
                        >
                          {r.deckName}
                        </Link>
                      ) : (
                        r.deckName
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#9a9182]">
                    {r.rank}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-bold font-display text-ink">
                    {r.points}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 py-8">
                    Nenhum deck deste subarquétipo registrado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
